import { currentOfflineOwner, keptAfterSwitch, ownerVerdict } from "./offlineOwner";

const DB_NAME = "trybeup_offline";
const DB_VERSION = 2;
const QUEUE_STORE = "sync_queue";
const WORKOUT_STORE = "offline_workouts";
const DRAFT_STORE = "workout_drafts";

type SyncStatus = "idle" | "syncing" | "error";
type QueuedRequest = {
  // number → IndexedDB autoincrement key; string ("ls-…") → localStorage
  // fallback entry. removeFromQueue/updateRetries route on this type.
  id: number | string;
  method: string;
  url: string;
  body: string | null;
  timestamp: number;
  retries: number;
  // GoTrue id of the account that queued it (lib/offlineOwner.ts). Absent on
  // entries queued by older bundles.
  ownerId?: string | null;
  // The device-local workout copy this entry finishes syncing. That copy is
  // deleted only once the server has answered for it (enqueueWorkoutSync).
  localWorkoutId?: string;
};

type EnqueueMeta = { localWorkoutId?: string };

// Same rules as lib/offlineQueue.ts (review 2026-10-17). Only a server error
// (5xx) spends one of the MAX_SERVER_RETRIES attempts. A network failure — no
// response at all: airplane mode, or gym Wi-Fi with a captive portal, where
// navigator.onLine is true but every fetch fails — is not a verdict on the
// request and costs nothing; it used to count, so a workout queued offline was
// dropped after six 15-second retries (~90 s) on captive-portal Wi-Fi, with
// its local copy already deleted. Instead an entry ages out after a week.
const MAX_SERVER_RETRIES = 5;
export const MAX_QUEUE_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** An idempotency key for a write the queue may replay. */
export function newClientId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

type OfflineSyncState = {
  isOnline: boolean;
  syncStatus: SyncStatus;
  pendingCount: number;
  lastSyncError: string | null;
};

type StateListener = (state: OfflineSyncState) => void;

export type WorkoutDraft = {
  workoutId: string;
  workoutName: string;
  workoutMode: string;
  exercises: unknown[];
  elapsed: number;
  startTime: number;
  timestamp: number;
};

let db: IDBDatabase | null = null;
let listeners: StateListener[] = [];
let currentState: OfflineSyncState = {
  isOnline: navigator.onLine,
  syncStatus: "idle",
  pendingCount: 0,
  lastSyncError: null,
};
let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
// Synchronous re-entrancy lock. The `syncStatus` state field can't guard
// replay because it was only set AFTER an `await getAllQueued()` — two replays
// fired in the same tick (e.g. initOfflineSync + the boot `online` event) both
// slipped past the check and double-sent every queued POST, duplicating rows.
let replaying = false;

function notifyListeners() {
  listeners.forEach((fn) => fn({ ...currentState }));
}

function updateState(partial: Partial<OfflineSyncState>) {
  currentState = { ...currentState, ...partial };
  notifyListeners();
}

function openDB(): Promise<IDBDatabase> {
  if (db) return Promise.resolve(db);
  return new Promise((resolve, reject) => {
    // Some WebView contexts (older iOS Private mode, certain Capacitor
    // configurations) don't expose IndexedDB at all. Without this
    // guard the bare `indexedDB.open(...)` below throws ReferenceError
    // — which is what Sentry caught on 2026-06-13 (issue
    // TRYBEUP-CAPACITOR-4). Rejecting with a named error here lets
    // existing callers' try/catch blocks (refreshPendingCount,
    // replayQueue, the component-side saveWorkoutLocally consumers)
    // degrade to online-only without a hard crash. Mirrors the same
    // guard already in lib/durableSessionStorage.ts:48.
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB-unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const d = req.result;
      const oldVersion = (event as IDBVersionChangeEvent).oldVersion;
      if (oldVersion < 1) {
        if (!d.objectStoreNames.contains(QUEUE_STORE)) {
          d.createObjectStore(QUEUE_STORE, { keyPath: "id", autoIncrement: true });
        }
        if (!d.objectStoreNames.contains(WORKOUT_STORE)) {
          d.createObjectStore(WORKOUT_STORE, { keyPath: "workoutId" });
        }
      }
      if (oldVersion < 2) {
        if (!d.objectStoreNames.contains(DRAFT_STORE)) {
          d.createObjectStore(DRAFT_STORE, { keyPath: "workoutId" });
        }
      }
    };
    req.onsuccess = () => {
      db = req.result;
      // Sign-out deletes this database; an open connection would block that
      // delete until the page unloads (and then wipe the NEXT user's queue).
      // Close on the version-change signal and reopen lazily.
      db.onversionchange = () => {
        try { db?.close(); } catch { /* ignore */ }
        db = null;
      };
      resolve(db);
    };
    req.onerror = () => reject(req.error);
  });
}

// ── localStorage fallback queue ──────────────────────────────────────────
// Some WebView / Private-mode contexts don't expose IndexedDB at all, so
// openDB() rejects with 'indexedDB-unavailable' and a bare enqueue() throws —
// which is what surfaced the hard "Couldn't save set" error to the founder
// (2026-07-01) even though nothing was wrong with the data. Mirror the queue
// in localStorage so a write always has somewhere durable to land; replay
// drains both stores. Only when BOTH IndexedDB AND localStorage are
// unavailable (truly no storage) does a write fail — the one case worth
// surfacing.
const FALLBACK_KEY = "trybeup_offline_fallback_queue";

function lsGetAll(): QueuedRequest[] {
  try {
    const raw = localStorage.getItem(FALLBACK_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function lsWriteAll(items: QueuedRequest[]): boolean {
  try {
    localStorage.setItem(FALLBACK_KEY, JSON.stringify(items));
    return true;
  } catch {
    return false;
  }
}

function lsAdd(method: string, url: string, body: unknown | null, meta: EnqueueMeta = {}): boolean {
  const items = lsGetAll();
  items.push({
    id: `ls-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    method,
    url,
    body: body ? JSON.stringify(body) : null,
    timestamp: Date.now(),
    retries: 0,
    ownerId: currentOfflineOwner(),
    ...meta,
  });
  return lsWriteAll(items);
}

function lsRemove(id: string): void {
  lsWriteAll(lsGetAll().filter((i) => i.id !== id));
}

function lsSetRetries(id: string, retries: number): void {
  lsWriteAll(lsGetAll().map((i) => (i.id === id ? { ...i, retries } : i)));
}

async function enqueue(method: string, url: string, body: unknown | null, meta: EnqueueMeta = {}): Promise<void> {
  // Primary store: IndexedDB. If it's unavailable or the write fails, fall
  // back to the localStorage mirror so the action still persists.
  try {
    const d = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = d.transaction(QUEUE_STORE, "readwrite");
      const store = tx.objectStore(QUEUE_STORE);
      store.add({
        method,
        url,
        body: body ? JSON.stringify(body) : null,
        timestamp: Date.now(),
        retries: 0,
        ownerId: currentOfflineOwner(),
        ...meta,
      });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    refreshPendingCount();
    return;
  } catch {
    if (lsAdd(method, url, body, meta)) {
      refreshPendingCount();
      return;
    }
    // Both stores unavailable — nothing can persist. Surface it so the caller
    // can tell the user honestly instead of silently dropping the write.
    throw new Error("offline-storage-unavailable");
  }
}

async function getAllQueued(): Promise<QueuedRequest[]> {
  // IndexedDB items first, then the localStorage fallback. If IndexedDB is
  // unavailable we still return the fallback items so they replay — never
  // throw, or replayQueue would abandon queued writes on IDB-less devices.
  let idbItems: QueuedRequest[] = [];
  try {
    const d = await openDB();
    idbItems = await new Promise<QueuedRequest[]>((resolve, reject) => {
      const tx = d.transaction(QUEUE_STORE, "readonly");
      const store = tx.objectStore(QUEUE_STORE);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } catch {
    idbItems = [];
  }
  return [...idbItems, ...lsGetAll()];
}

async function removeFromQueue(id: number | string): Promise<void> {
  if (typeof id === "string") {
    lsRemove(id);
    refreshPendingCount();
    return;
  }
  const d = await openDB();
  return new Promise((resolve, reject) => {
    const tx = d.transaction(QUEUE_STORE, "readwrite");
    const store = tx.objectStore(QUEUE_STORE);
    store.delete(id);
    tx.oncomplete = () => {
      refreshPendingCount();
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

async function updateRetries(id: number | string, retries: number): Promise<void> {
  if (typeof id === "string") {
    lsSetRetries(id, retries);
    return;
  }
  const d = await openDB();
  return new Promise((resolve, reject) => {
    const tx = d.transaction(QUEUE_STORE, "readwrite");
    const store = tx.objectStore(QUEUE_STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const item = getReq.result;
      if (item) {
        item.retries = retries;
        store.put(item);
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function refreshPendingCount() {
  try {
    const current = currentOfflineOwner();
    const items = (await getAllQueued()).filter((i) => ownerVerdict(i.ownerId, current) !== "purge");
    updateState({ pendingCount: items.length });
  } catch {
    updateState({ pendingCount: 0 });
  }
}

function scheduleRetry(delayMs: number = 15000) {
  if (retryTimeoutId) clearTimeout(retryTimeoutId);
  retryTimeoutId = setTimeout(() => {
    retryTimeoutId = null;
    if (navigator.onLine) replayQueue();
  }, delayMs);
}

async function replayQueue(): Promise<void> {
  // Set the lock BEFORE any await so a concurrent call can't slip past.
  if (replaying) return;
  if (!navigator.onLine) return;
  replaying = true;
  try {
  const items = await getAllQueued();
  if (items.length === 0) {
    updateState({ syncStatus: "idle", pendingCount: 0 });
    return;
  }

  updateState({ syncStatus: "syncing", lastSyncError: null });

  const sorted = items.sort((a, b) => a.timestamp - b.timestamp);
  let hadFailure = false;
  // Count items PERMANENTLY dropped this pass (rejected 4xx, or 5xx/network
  // past the retry cap). Previously these were removed silently — a workout
  // the user was told "will sync when you're back online" could vanish with no
  // signal (audit 2026-08-08). We surface it below so the user knows.
  let droppedThisPass = 0;

  const owner = currentOfflineOwner();
  for (const item of sorted) {
    // Someone else's write (one account replaced another without a sign-out)
    // is never sent with this session — dropped quietly, it isn't this
    // person's loss to report. Owned but nobody signed in: wait.
    const verdict = ownerVerdict(item.ownerId, owner);
    if (verdict === "purge") {
      await removeFromQueue(item.id);
      await forgetLocalWorkout(item);
      continue;
    }
    if (verdict === "hold") continue;
    // A week old: the day it was for is long gone and nothing else would ever
    // age it out now that network failures are free. Its local workout copy
    // stays on the phone.
    if (item.timestamp && Date.now() - item.timestamp > MAX_QUEUE_AGE_MS) {
      await removeFromQueue(item.id);
      droppedThisPass++;
      continue;
    }
    let res: Response;
    try {
      const headers: Record<string, string> = {};
      if (item.body) headers["Content-Type"] = "application/json";

      const { apiFetch } = await import("./api");
      res = await apiFetch(item.url, {
        method: item.method,
        headers,
        body: item.body,
      });
    } catch {
      // No response at all (offline, captive portal, a timeout). Keep it and
      // don't count an attempt. Stop the pass: the rest would fail the same
      // way, and stopping keeps a workout's queued steps in order.
      hadFailure = true;
      break;
    }
    try {
      if (res.ok || res.status === 404 || res.status === 409) {
        // The server has answered for it — only now may the local copy go.
        await removeFromQueue(item.id);
        await forgetLocalWorkout(item);
      } else if (res.status === 401 || res.status === 408 || res.status === 429) {
        // Token refresh raced the reconnect, or a timeout / rate limit —
        // retry next pass without spending one of the attempts.
        hadFailure = true;
      } else if (res.status >= 500) {
        if (item.retries >= MAX_SERVER_RETRIES) {
          await removeFromQueue(item.id);
          droppedThisPass++;
        } else {
          await updateRetries(item.id, item.retries + 1);
          hadFailure = true;
        }
      } else {
        // 4xx (other than 404/409 handled above) — the server rejected it and
        // a retry won't help. Drop, but count it as a real loss to surface.
        await removeFromQueue(item.id);
        droppedThisPass++;
      }
    } catch {
      // Local storage hiccup while bookkeeping — leave the entry for the next
      // pass rather than lose it.
      hadFailure = true;
    }
  }

  if (droppedThisPass > 0) {
    // Fire-and-forget signal for the UI (App.tsx listens and toasts). A queue
    // lib has no toast access, so use a window event rather than reaching into
    // React. Never throw from here.
    try {
      window.dispatchEvent(
        new CustomEvent("trybeup:offline-sync-dropped", { detail: { count: droppedThisPass } }),
      );
    } catch { /* CustomEvent unsupported — non-fatal */ }
  }

  await refreshPendingCount();

  if (hadFailure && currentState.pendingCount > 0) {
    updateState({ syncStatus: "error", lastSyncError: "Some changes failed to sync. Retrying..." });
    scheduleRetry();
  } else {
    updateState({ syncStatus: "idle" });
  }
  } finally {
    replaying = false;
  }
}

async function forgetLocalWorkout(item: QueuedRequest): Promise<void> {
  if (!item.localWorkoutId) return;
  try {
    await removeOfflineWorkout(item.localWorkoutId);
  } catch { /* storage unavailable — a stale copy is harmless */ }
}

export async function saveWorkoutLocally(workoutId: string, data: unknown): Promise<void> {
  const d = await openDB();
  return new Promise((resolve, reject) => {
    const tx = d.transaction(WORKOUT_STORE, "readwrite");
    const store = tx.objectStore(WORKOUT_STORE);
    store.put({ workoutId, data, timestamp: Date.now(), ownerId: currentOfflineOwner() });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getOfflineWorkout(workoutId: string): Promise<unknown | null> {
  const d = await openDB();
  return new Promise((resolve, reject) => {
    const tx = d.transaction(WORKOUT_STORE, "readonly");
    const store = tx.objectStore(WORKOUT_STORE);
    const req = store.get(workoutId);
    req.onsuccess = () => resolve(req.result?.data ?? null);
    req.onerror = () => reject(req.error);
  });
}

export async function removeOfflineWorkout(workoutId: string): Promise<void> {
  const d = await openDB();
  return new Promise((resolve, reject) => {
    const tx = d.transaction(WORKOUT_STORE, "readwrite");
    const store = tx.objectStore(WORKOUT_STORE);
    store.delete(workoutId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function saveWorkoutDraft(draft: WorkoutDraft): Promise<void> {
  const d = await openDB();
  return new Promise((resolve, reject) => {
    const tx = d.transaction(DRAFT_STORE, "readwrite");
    const store = tx.objectStore(DRAFT_STORE);
    store.put({ ...draft, timestamp: Date.now(), ownerId: currentOfflineOwner() });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getWorkoutDraft(workoutId: string): Promise<WorkoutDraft | null> {
  const d = await openDB();
  return new Promise((resolve, reject) => {
    const tx = d.transaction(DRAFT_STORE, "readonly");
    const store = tx.objectStore(DRAFT_STORE);
    const req = store.get(workoutId);
    req.onsuccess = () => {
      const draft = req.result as (WorkoutDraft & { ownerId?: string | null }) | undefined;
      // Another account's draft is not this person's to restore.
      resolve(draft && ownerVerdict(draft.ownerId, currentOfflineOwner()) === "replay" ? draft : null);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function removeWorkoutDraft(workoutId: string): Promise<void> {
  const d = await openDB();
  return new Promise((resolve, reject) => {
    const tx = d.transaction(DRAFT_STORE, "readwrite");
    const store = tx.objectStore(DRAFT_STORE);
    store.delete(workoutId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function enqueueWorkoutSync(workoutId: string): Promise<void> {
  const offlineData = await getOfflineWorkout(workoutId);
  if (!offlineData) return;

  const workout = offlineData as {
    workoutName?: string;
    workoutMode?: string;
    exercises: Array<{
      id?: string;
      exerciseName: string;
      category: string;
      orderIndex: number;
      notes: string;
      sets: Array<{
        setNumber: number;
        setType: string;
        reps: string;
        weight: string;
        weightUnit: string;
        duration: string;
        distance: string;
        distanceUnit: string;
        rpe: string;
        rir: string;
        completed: boolean;
      }>;
    }>;
    elapsed: number;
    completedAt: string;
  };

  const isFullyOffline = workoutId.startsWith("offline_workout_");

  if (isFullyOffline) {
    const exercisesPayload = workout.exercises.map(exercise => ({
      exerciseName: exercise.exerciseName,
      category: exercise.category,
      orderIndex: exercise.orderIndex,
      sets: exercise.sets
        .filter(s => s.reps || s.duration || s.weight || s.distance)
        .map(s => ({
          setNumber: s.setNumber,
          setType: s.setType,
          reps: s.reps ? parseInt(s.reps) : null,
          weight: s.weight ? parseFloat(s.weight) : null,
          weightUnit: s.weightUnit,
          duration: s.duration ? parseInt(s.duration) : null,
          distance: s.distance ? Math.round(parseFloat(s.distance)) : null,
          distanceUnit: s.distanceUnit,
          rpe: s.rpe ? parseFloat(s.rpe) : null,
          rir: s.rir ? parseInt(s.rir) : null,
          completed: s.completed,
        })),
    }));

    await enqueue("POST", "/api/workouts/offline-sync", {
      // Stable client id so a replayed sync (lost response on reconnect →
      // retry) returns the already-synced workout instead of creating a
      // duplicate. `workoutId` is the device-local 'offline_workout_...' id and
      // is constant across retries of this queued item.
      clientWorkoutId: workoutId,
      name: workout.workoutName || "Offline Workout",
      type: "strength",
      mode: workout.workoutMode || "strong",
      exercises: exercisesPayload,
      completedAt: workout.completedAt,
      duration: workout.elapsed,
    }, { localWorkoutId: workoutId });
  } else {
    for (const exercise of workout.exercises) {
      const isOfflineExercise = exercise.id?.startsWith("offline_") || !exercise.id;
      if (isOfflineExercise) {
        const setsPayload = exercise.sets
          .filter(s => s.reps || s.duration || s.weight || s.distance)
          .map(s => ({
            setNumber: s.setNumber,
            setType: s.setType,
            reps: s.reps ? parseInt(s.reps) : null,
            weight: s.weight ? parseFloat(s.weight) : null,
            weightUnit: s.weightUnit,
            duration: s.duration ? parseInt(s.duration) : null,
            distance: s.distance ? Math.round(parseFloat(s.distance)) : null,
            distanceUnit: s.distanceUnit,
            rpe: s.rpe ? parseFloat(s.rpe) : null,
            rir: s.rir ? parseInt(s.rir) : null,
            completed: s.completed,
          }));

        await enqueue("POST", `/api/workouts/${workoutId}/exercises-with-sets`, {
          exerciseName: exercise.exerciseName,
          category: exercise.category,
          orderIndex: exercise.orderIndex,
          // Idempotency key: the queue can replay this POST after a lost
          // response; the server dedupes on it so the exercise isn't duplicated.
          clientExerciseId: newClientId(),
          sets: setsPayload,
        });
      }
    }

    await enqueue("PUT", `/api/workouts/${workoutId}`, {
      completedAt: workout.completedAt,
      duration: workout.elapsed,
    }, { localWorkoutId: workoutId });
  }
  // The local copy is NOT removed here: it goes when the server confirms the
  // entry tagged with it (replayQueue). Until then it's the only full record
  // of the workout on this phone (review 2026-10-17).
}

/**
 * A detected account switch (hooks/useAuth.ts): drop every queued write and
 * local workout copy not stamped with `userId` — ownerless legacy entries
 * included, since they were queued before the switch (lib/offlineOwner.ts).
 */
export async function purgeOfflineDataNotOwnedBy(userId: string): Promise<void> {
  lsWriteAll(lsGetAll().filter((i) => keptAfterSwitch(i.ownerId, userId)));
  try {
    const d = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = d.transaction([QUEUE_STORE, WORKOUT_STORE, DRAFT_STORE], "readwrite");
      for (const name of [QUEUE_STORE, WORKOUT_STORE, DRAFT_STORE]) {
        const store = tx.objectStore(name);
        const req = store.openCursor();
        req.onsuccess = () => {
          const cursor = req.result;
          if (!cursor) return;
          const row = cursor.value as { ownerId?: unknown };
          if (!keptAfterSwitch(row?.ownerId, userId)) cursor.delete();
          cursor.continue();
        };
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch { /* no IndexedDB — nothing stored there */ }
  await refreshPendingCount();
}

export async function offlineApiRequest(
  method: string,
  url: string,
  data?: unknown,
): Promise<Response> {
  const { apiFetch } = await import("./api");
  if (method === "GET") {
    return apiFetch(url);
  }

  try {
    const headers: Record<string, string> = {};
    if (data) headers["Content-Type"] = "application/json";

    const res = await apiFetch(url, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!res.ok) {
      // Only queue server errors (5xx) for offline retry — client errors (4xx)
      // won't succeed on retry so let them propagate immediately.
      if (res.status >= 500) {
        await enqueue(method, url, data ?? null);
        return new Response(JSON.stringify({ queued: true, offline: true }), {
          status: 202,
          headers: { "Content-Type": "application/json" },
        });
      }
      // Carry the server's reason, don't drop it. This line used to throw
      // `${status}: ${res.statusText}` — literally "422: Unprocessable
      // Entity", which names no field and no value. A Sentry issue with 145
      // events sat on exactly that string and could not be diagnosed from it,
      // because the one thing needed (which input the server rejected) was in
      // the response body we were throwing away. `throwIfNotOk` already reads
      // that body and, since this change, renders a validation array as a
      // sentence — so use it instead of a second, worse implementation.
      const { throwIfNotOk } = await import("./api");
      await throwIfNotOk(res);
      // throwIfNotOk always throws for a non-ok response; this is unreachable
      // and exists only so the type checker sees a terminated branch.
      throw new Error(`${res.status}: ${res.statusText}`);
    }
    return res;
  } catch (err) {
    // Network errors (no response at all) — queue for offline sync
    if (err instanceof TypeError) {
      await enqueue(method, url, data ?? null);
      return new Response(JSON.stringify({ queued: true, offline: true }), {
        status: 202,
        headers: { "Content-Type": "application/json" },
      });
    }
    throw err;
  }
}

export function subscribeToOfflineState(listener: StateListener): () => void {
  listeners.push(listener);
  listener({ ...currentState });
  return () => {
    listeners = listeners.filter((fn) => fn !== listener);
  };
}

export function getOfflineState(): OfflineSyncState {
  return { ...currentState };
}

export function initOfflineSync() {
  const handleOnline = () => {
    updateState({ isOnline: true });
    replayQueue();
  };

  const handleOffline = () => {
    updateState({ isOnline: false });
  };

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  updateState({ isOnline: navigator.onLine });
  refreshPendingCount();

  if (navigator.onLine) {
    replayQueue();
  }
}
